/** The dense "related records" strip under the tour-operator header.
 *
 *  An operator is the tenant of about twenty console grids, and until now the
 *  page offered no way into any of them — you went to /bookings and picked the
 *  operator again. The strip groups them (People · Bookings & orders · Catalog
 *  · Messaging · Integrations · Ops · Licensees) so each bucket is one hover
 *  away rather than twenty links wide.
 *
 *  The second row renders the payload fields the page never showed at all —
 *  the franchise parent and its licensees, the accounting company, the default
 *  staff / guest access groups, the default launch policy, the operator's tags,
 *  the logo and payment URLs — and a third the content refs (refund policy,
 *  review, photo sharing, app promo image), which have no console page and so
 *  only copy. Absent parts are skipped, so a plain operator shows only the
 *  strip. */
import { Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { TourOperatorDetail } from '@/api/tourOperators';
import { DetailSubtitle, ValueActions } from '@/components/ValueActions/ValueActions';

import {
  accessGroupActions,
  childActions,
  companyActions,
  contentRefActions,
  launchPolicyActions,
  operatorTagActions,
  parentActions,
  relatedGroups,
  sandboxPairActions,
  urlActions,
  type TenantCtx,
} from '../tenantLinks';

/** Tags are ids only (`tag_ids`) — more than a handful is a wall of numbers. */
const TAG_CHIP_CAP = 8;

/** The content refs the payload carries as `<name>_id` + `<name>_label` pairs.
 *  None of them has a console page, so the id and the name are all there is to
 *  offer — but "which refund policy does this operator use?" was previously
 *  unanswerable without the Django admin. */
const CONTENT_REFS = [
  ['refund_policy', 'Refund policy'],
  ['review', 'Review'],
  ['photo_sharing', 'Photo sharing'],
  ['app_promo_image', 'App promo image'],
] as const;

export function TenantRelated({ to }: { to: TourOperatorDetail }) {
  const ctx: TenantCtx = { id: to.id, code: to.code };
  const groups = relatedGroups(ctx, { isFranchise: to.is_franchise });

  const payloadParts = [
    // The paired operator first (SBX D8 "a link to the paired operator"): a sandbox and its production
    // operator are easy to mistake for each other by name.
    to.sandbox_of && (
      <ValueActions key="sandbox-of" actions={sandboxPairActions(to.sandbox_of, 'production')}>
        Sandbox of:{' '}
        <Link component={RouterLink} to={`/tour-operators/${to.sandbox_of.id}`} underline="hover">
          {to.sandbox_of.name || to.sandbox_of.code}
        </Link>
      </ValueActions>
    ),
    to.sandbox && (
      <ValueActions key="sandbox" actions={sandboxPairActions(to.sandbox, 'sandbox')}>
        Sandbox:{' '}
        <Link component={RouterLink} to={`/tour-operators/${to.sandbox.id}`} underline="hover">
          {to.sandbox.code}
        </Link>
      </ValueActions>
    ),
    to.parent_id != null && (
      <ValueActions key="parent" actions={parentActions({ id: to.parent_id, code: to.parent_code })}>
        Parent:{' '}
        <Link component={RouterLink} to={`/tour-operators/${to.parent_id}`} underline="hover">
          {to.parent_name ?? to.parent_code ?? `#${to.parent_id}`}
        </Link>
      </ValueActions>
    ),
    to.children.length > 0 && (
      <Stack key="children" component="span" direction="row" spacing={0.5} display="inline-flex" alignItems="baseline">
        <span>Licensees:</span>
        {to.children.map((c) => (
          <ValueActions key={c.id} actions={childActions(c)}>
            {c.name || c.code}
          </ValueActions>
        ))}
      </Stack>
    ),
    to.company_id != null && (
      <ValueActions key="company" actions={companyActions({ id: to.company_id, label: to.company_label })}>
        Company: {to.company_label ?? `#${to.company_id}`}
      </ValueActions>
    ),
    to.default_staff_group_id != null && (
      <ValueActions
        key="staff-group"
        actions={accessGroupActions({ id: to.default_staff_group_id, label: to.default_staff_group_label })}
      >
        Staff group: {to.default_staff_group_label ?? `#${to.default_staff_group_id}`}
      </ValueActions>
    ),
    to.guest_group_id != null && (
      <ValueActions
        key="guest-group"
        actions={accessGroupActions({ id: to.guest_group_id, label: to.guest_group_label })}
      >
        Guest group: {to.guest_group_label ?? `#${to.guest_group_id}`}
      </ValueActions>
    ),
    to.default_launch_policy_id != null && (
      <ValueActions
        key="launch-policy"
        actions={launchPolicyActions(
          { id: to.default_launch_policy_id, label: to.default_launch_policy_label },
          ctx,
        )}
      >
        Launch policy: {to.default_launch_policy_label ?? `#${to.default_launch_policy_id}`}
      </ValueActions>
    ),
    to.tag_ids.length > 0 && (
      <Stack key="tags" component="span" direction="row" spacing={0.5} display="inline-flex" alignItems="baseline">
        <span>Tags:</span>
        {to.tag_ids.slice(0, TAG_CHIP_CAP).map((id) => (
          <ValueActions key={id} actions={operatorTagActions(id, ctx)}>
            #{id}
          </ValueActions>
        ))}
        {to.tag_ids.length > TAG_CHIP_CAP && <span>+{to.tag_ids.length - TAG_CHIP_CAP} more</span>}
      </Stack>
    ),
    to.nf_logo_url && (
      <ValueActions key="logo" actions={urlActions(to.nf_logo_url, 'Open logo')}>
        Logo
      </ValueActions>
    ),
    to.payment_url && (
      <ValueActions key="payment" actions={urlActions(to.payment_url, 'Open payment page')}>
        Payment page
      </ValueActions>
    ),
  ];

  const contentParts = CONTENT_REFS.flatMap(([key, label]) => {
    const id = to[`${key}_id` as keyof TourOperatorDetail] as number | null;
    if (id == null) return [];
    const name = to[`${key}_label` as keyof TourOperatorDetail] as string | null;
    return [
      <ValueActions key={key} actions={contentRefActions(id, name)}>
        {label}: {name ?? `#${id}`}
      </ValueActions>,
    ];
  });

  const hasPayloadParts = payloadParts.some(Boolean);

  return (
    <Stack spacing={0.25} sx={{ mt: -1.5, mb: 2 }}>
      <Typography variant="body2" color="text.secondary" component="div">
        <DetailSubtitle
          parts={groups.map((g) => (
            <ValueActions key={g.key} actions={g.actions}>
              {g.label}
            </ValueActions>
          ))}
        />
      </Typography>
      {hasPayloadParts && (
        <Typography variant="body2" color="text.secondary" component="div">
          <DetailSubtitle parts={payloadParts} />
        </Typography>
      )}
      {contentParts.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          <DetailSubtitle parts={contentParts} />
        </Typography>
      )}
    </Stack>
  );
}
